import * as Sentry from '@sentry/react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useState } from 'react'
import {
  Avatar,
  AvatarFallback,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldError,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@repo/ui'
import { ChevronDown, EllipsisVertical } from 'lucide-react'
import { PageContent } from '@/components/PageContent'
import { PageHeader } from '@/components/PageHeader'
import type { OrgUser } from '@/api/org/users'
import { InviteUserRequestSchema, getOrgUsers, inviteUser, removeUser } from '@/api/org/users'
import { demoteAdmin, promoteAdmin } from '@/api/org/admin'
import { demoteOwner, promoteOwner } from '@/api/org/owner'
import { zodFieldValidator } from '@/common/parseDataType'
import type { ChangeEvent } from 'react'

export const Route = createFileRoute('/_auth/$orgSlug/users/')({
  loader: async () => ({ users: await getOrgUsers() }),
  component: RouteComponent,
  head: () => ({ meta: [{ title: 'Users - Snowvault' }] })
})

function formatLastSeen(dateStr: string | null): string {
  if (!dateStr) return 'never'
  const date = new Date(dateStr)
  if (!Number.isFinite(date.getTime())) return 'unknown'
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function RouteComponent() {
  const router = useRouter()
  const { userOrgProfile } = Route.useRouteContext().user
  const { users } = Route.useLoaderData()

  const isAdmin = userOrgProfile?.org.admin === true
  const isOwner = userOrgProfile?.org.owner === true
  const currentUserId = userOrgProfile?.uid

  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<number | null>(null)
  const [editUser, setEditUser] = useState<OrgUser | null>(null)
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<string | null>(null)

  async function handleMutation(key: string, fn: () => Promise<void>) {
    setPendingAction(key)
    setMutationError(null)
    try {
      await fn()
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { userId: currentUserId } })
      setMutationError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <>
      <PageHeader breadcrumbs={[{ label: 'Users' }]} />
      <PageContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {isAdmin ? 'Manage users, roles and permissions.' : 'Please contact an admin to manage users.'}
            </p>
            {isAdmin && (
              <Button size="sm" onClick={() => setShowInviteDialog(true)}>
                Invite User
              </Button>
            )}
          </div>

          {mutationError && <p className="text-sm text-destructive">{mutationError}</p>}

          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users found.</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableBody>
                  {users.map((user) => (
                    <UserRow
                      key={user.UserID}
                      user={user}
                      currentUserId={currentUserId}
                      isAdmin={isAdmin}
                      onEdit={() => setEditUser(user)}
                      onRemove={() => setConfirmRemoveUserId(user.UserID)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </PageContent>

      <EditUserDialog
        key={editUser?.UserID}
        user={editUser}
        isAdmin={isAdmin}
        isOwner={isOwner}
        isPending={pendingAction?.startsWith('edit-') ?? false}
        onSave={async (newOwner, newAdmin) => {
          if (!editUser) return
          const calls: Array<() => Promise<void>> = []
          if (newOwner !== editUser.Owner) {
            calls.push(
              newOwner
                ? () => promoteOwner({ userId: editUser.UserID })
                : () => demoteOwner({ userId: editUser.UserID })
            )
          }
          if (newAdmin !== editUser.Admin) {
            calls.push(
              newAdmin
                ? () => promoteAdmin({ userId: editUser.UserID })
                : () => demoteAdmin({ userId: editUser.UserID })
            )
          }
          setPendingAction(`edit-${editUser.UserID}`)
          try {
            for (const call of calls) await call()
            setEditUser(null)
          } finally {
            await router.invalidate()
            setPendingAction(null)
          }
        }}
        onClose={() => setEditUser(null)}
      />

      <RemoveUserDialog
        open={typeof confirmRemoveUserId === 'number'}
        isPending={pendingAction === `remove-${confirmRemoveUserId}`}
        onConfirm={() =>
          handleMutation(`remove-${confirmRemoveUserId}`, async () => {
            await removeUser({ userId: confirmRemoveUserId! })
            setConfirmRemoveUserId(null)
          })
        }
        onCancel={() => setConfirmRemoveUserId(null)}
      />

      <InviteUserDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSuccess={async () => {
          setShowInviteDialog(false)
          await router.invalidate()
        }}
      />
    </>
  )
}

function UserRow({
  user,
  currentUserId,
  isAdmin,
  onEdit,
  onRemove
}: {
  user: OrgUser
  currentUserId: number | undefined
  isAdmin: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const isSelf = user.UserID === currentUserId
  const isPendingInvite = !user.LastLoggedIn
  const initials = `${user.FirstName.charAt(0)}${(user.Surname ?? '').charAt(0)}`.toUpperCase()

  return (
    <TableRow className="[&_td]:p-3 [&_td]:align-middle">
      <TableCell className="w-10">
        <Avatar className="size-8">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
      </TableCell>
      <TableCell className="min-w-48 pl-1!">
        <div className="flex flex-col">
          <span className="font-medium">
            {user.FirstName} {user.Surname}
          </span>
          <span className="text-[11px] text-muted-foreground/60">
            Last signed in {formatLastSeen(user.LastLoggedIn)}
          </span>
        </div>
      </TableCell>
      <TableCell className="w-full">
        <span className="text-sm text-muted-foreground">{user.Email}</span>
      </TableCell>
      <TableCell>
        {isPendingInvite ? (
          <span className="text-sm text-amber-600 dark:text-amber-400">Pending invite</span>
        ) : (
          <span className="text-sm text-muted-foreground">{user.Owner ? 'Owner' : 'Member'}</span>
        )}
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">{user.Admin ? 'Admin' : 'Editor'}</span>
      </TableCell>
      <TableCell>
        {isAdmin && !isSelf && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="User actions">
                <EllipsisVertical className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onEdit}>Edit user</DropdownMenuItem>
              <DropdownMenuItem onClick={onRemove}>Remove user</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}

function RoleDropdown({
  value,
  disabled,
  onChange,
  options
}: {
  value: string
  disabled: boolean
  onChange: (value: string) => void
  options: Array<{ value: string; label: string; description: string }>
}) {
  const selected = options.find((o) => o.value === value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="w-full justify-between">
          <span>{selected?.label ?? ''}</span>
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-(--radix-dropdown-menu-trigger-width)">
        {options.map((option) => (
          <DropdownMenuItem key={option.value} onClick={() => onChange(option.value)}>
            <div>
              <strong className="text-sm">{option.label}</strong>
              <p className="text-xs text-muted-foreground">{option.description}</p>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function EditUserDialog({
  user,
  isAdmin,
  isOwner,
  isPending,
  onSave,
  onClose
}: {
  user: OrgUser | null
  isAdmin: boolean
  isOwner: boolean
  isPending: boolean
  onSave: (newOwner: boolean, newAdmin: boolean) => Promise<void>
  onClose: () => void
}) {
  const [ownerValue, setOwnerValue] = useState<string>(user ? (user.Owner ? 'owner' : 'member') : '')
  const [adminValue, setAdminValue] = useState<string>(user ? (user.Admin ? 'admin' : 'editor') : '')
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    if (!user) return
    setError(null)
    try {
      await onSave(ownerValue === 'owner', adminValue === 'admin')
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  return (
    <Dialog
      open={!!user}
      onOpenChange={(o: boolean) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>{user ? `${user.FirstName} ${user.Surname ?? ''}`.trim() : ''}</DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="space-y-4">
          {isOwner && (
            <div className="space-y-1.5">
              <Label>Membership</Label>
              <RoleDropdown
                value={ownerValue}
                disabled={isPending}
                onChange={setOwnerValue}
                options={[
                  { value: 'owner', label: 'Owner', description: 'Can manage the organisation' },
                  { value: 'member', label: 'Member', description: 'Member of the organisation' }
                ]}
              />
            </div>
          )}

          {isAdmin && (
            <div className="space-y-1.5">
              <Label>Role</Label>
              <RoleDropdown
                value={adminValue}
                disabled={isPending}
                onChange={setAdminValue}
                options={[
                  { value: 'admin', label: 'Admin', description: 'Editor + manage users and settings' },
                  { value: 'editor', label: 'Editor', description: 'Can manage content and use agents' }
                ]}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RemoveUserDialog({
  open,
  isPending,
  onConfirm,
  onCancel
}: {
  open: boolean
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove User</DialogTitle>
          <DialogDescription className="sr-only">Remove User Modal</DialogDescription>
        </DialogHeader>
        <p className="text-sm">Are you sure you want to remove this user? This action cannot be undone.</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function InviteUserDialog({
  open,
  onClose,
  onSuccess
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => Promise<void>
}) {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { first_name: '', surname: '', email: '' },
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      try {
        await inviteUser({
          first_name: value.first_name,
          surname: value.surname || undefined,
          email: value.email
        })
        form.reset()
        await onSuccess()
      } catch (err) {
        Sentry.captureException(err)
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  function handleClose() {
    form.reset()
    setSubmitError(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription className="sr-only">Invite a new user to the organisation</DialogDescription>
        </DialogHeader>

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field
            name="first_name"
            validators={{ onChange: zodFieldValidator(InviteUserRequestSchema.shape.first_name) }}
          >
            {(field) => (
              <Field>
                <Input
                  placeholder="First Name"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Field
            name="surname"
            validators={{ onChange: zodFieldValidator(InviteUserRequestSchema.shape.surname) }}
          >
            {(field) => (
              <Field>
                <Input
                  placeholder="Surname"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Field name="email" validators={{ onChange: zodFieldValidator(InviteUserRequestSchema.shape.email) }}>
            {(field) => (
              <Field>
                <Input
                  placeholder="Email address"
                  type="email"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={form.state.isSubmitting}>
              Close
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Invite'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
